import { useEffect } from 'react';
import { AxiosError } from 'axios';
import { apiClient, type ApiErrorResponse } from './api-client';
import { useToast } from '@/components/ui/Toast';

/**
 * Component that attaches an Axios response interceptor to show
 * error toasts globally for API failures.
 *
 * Must be rendered inside both QueryClientProvider and ToastProvider.
 */
export function ApiToastInterceptor() {
  const { toast } = useToast();

  useEffect(() => {
    const interceptorId = apiClient.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ApiErrorResponse>) => {
        // Don't show toasts for 401 (handled by auth flow) or cancelled requests
        if (error.response?.status === 401 || error.code === 'ERR_CANCELED') {
          return Promise.reject(error);
        }

        // Extract the error message from the API response
        const apiError = error.response?.data?.error;
        const message = apiError?.message || error.message || 'An unexpected error occurred';

        // Don't toast network errors during page load (ECONNREFUSED etc)
        if (!error.response && error.code === 'ERR_NETWORK') {
          return Promise.reject(error);
        }

        toast({
          variant: 'error',
          title: 'Error',
          description: message,
          duration: 6000,
        });

        return Promise.reject(error);
      },
    );

    return () => {
      apiClient.interceptors.response.eject(interceptorId);
    };
  }, [toast]);

  return null;
}
